// StoryStrip (job page): how a job unfolded, drawn in one line under its title. Each key moment is a
// stop (a dot, hollow for one that was undone); each visit is a bar from when its techs arrived to when
// they stopped, in their pens (hatched once it's over, filled while it's on); the line is dashed where
// the job sat waiting, and runs on dashed past the last stop while it waits now. Each stop's name is a
// button that opens its row in the history; the day goes under the first stop of each day. "Full
// history · N" ends the line. On a phone the line scrolls sideways and starts at the latest stop.
// Reads only --story-*. Its pens are the board's (story-pen-1 … 8 are --bp-pen-1 … 8).
// Rich (round 2, Amir 26 Oct point 6): each stop also has one line under its day (who · how it ended,
// cut to the stop's width), and a
// stop given a card opens a HoverCard of its facts (no native title then); a booked visit still to come
// is drawn as the board draws a booked visit, its outline and nothing in it (isNext). A stop says what pressing it does (opens: "Open the visit").
import { useEffect, useRef, type ReactElement } from 'react';
import { HoverCard } from '../../components/HoverCard/HoverCard.tsx';
import type { KeyValueItem } from '../../components/KeyValue/KeyValue.tsx';
import { Link } from '../../components/Link/Link.tsx';
import './StoryStrip.css';

export interface StoryStop {
  id: string;
  /** Its name on the line: "Parts in". */
  label: string;
  /** The first stop of a day carries the day: "Wed Oct 21". */
  day?: string;
  /** Read out and on hover: "Heater received, PO-3310 · Fri Oct 23, 2:10 p.m.". */
  says: string;
  /** A visit's bar, in its techs' pens (1–8). */
  pens?: number[];
  /** A visit going on now. */
  isNow?: boolean;
  /** A moment that was undone (cancelled): a hollow dot. */
  isGone?: boolean;
  /** The line after it is dashed: the job waited. */
  waitsAfter?: boolean;
  /** Rich: one line under the day, who · how it ended ("Sam and Lee · quoted"), or what's to come. */
  sub?: string;
  /** Its facts on hover and focus (a HoverCard), in place of the native title. */
  card?: KeyValueItem[];
  /** A booked visit still to come: its pen's outline, empty, as the board draws a booked visit. */
  isNext?: boolean;
  /** What pressing it does, read out ("Open the visit"); "Show it in the history" when not given. */
  opens?: string;
}

export interface StoryStripProps {
  stops: StoryStop[];
  /** The job is waiting now: the line runs on dashed past the last stop. */
  isWaiting?: boolean;
  /** How many rows the whole history has. */
  total: number;
  onStop: (id: string) => void;
  onAll: () => void;
  /** Each stop says its who and outcome too, under its day (round 2). */
  isRich?: boolean;
  /** A record's strip (round 2, critic 3): a visit is a stop like the others, a dot (a ring while it's
   * still to come), its state said in words under it; no pen bars, which read as empty progress bars. */
  visitsAsStops?: boolean;
}

export function StoryStrip({
  stops,
  isWaiting,
  total,
  onStop,
  onAll,
  isRich,
  visitsAsStops,
}: StoryStripProps) {
  const list = useRef<HTMLOListElement>(null);
  // on a phone the line scrolls; it starts at the latest moment
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [stops]);
  // one stop says nothing a heading doesn't; but the record's strip carries the visits' dates and the
  // next one (round 3), so there one visit is still drawn
  if (stops.length < (isRich ? 1 : 2)) return null;
  return (
    <nav className="fl-story" data-rich={isRich ? '' : undefined} aria-label="How the job unfolded">
      <ol ref={list} className="fl-story__line">
        {stops.map((s, i) => (
          <li
            key={s.id}
            className="fl-story__stop"
            data-visit={s.pens && !visitsAsStops ? '' : undefined}
            data-wait={s.waitsAfter || (i === stops.length - 1 && isWaiting) ? '' : undefined}
            data-last={i === stops.length - 1 ? '' : undefined}
          >
            {withCard(
              s,
              <button
                type="button"
                className="fl-story__btn"
                title={s.card ? undefined : s.says}
                aria-label={`${s.label}${s.isNow ? ', now' : ''}: ${s.says.replace(/\.$/, '')}. ${s.opens ?? 'Show it in the history'}`}
                onClick={() => onStop(s.id)}
              >
                <span className="fl-story__label">{s.isNow ? `${s.label} · now` : s.label}</span>
                {s.pens && !visitsAsStops ? (
                  <span
                    className="fl-story__bar"
                    data-now={s.isNow ? '' : undefined}
                    data-two={s.pens.length > 1 ? '' : undefined}
                    data-next={s.isNext ? '' : undefined}
                  >
                    {s.pens.map((p) => (
                      <i key={p} className="fl-story__pen" data-pen={p} />
                    ))}
                  </span>
                ) : (
                  <span
                    className="fl-story__node"
                    data-gone={s.isGone ? '' : undefined}
                    data-next={s.isNext ? '' : undefined}
                  />
                )}
                <span className="fl-story__day">{s.day ?? ''}</span>
                {isRich ? <span className="fl-story__sub">{s.sub ?? ''}</span> : null}
              </button>,
            )}
          </li>
        ))}
        <li
          className="fl-story__stop fl-story__tail"
          data-wait={isWaiting ? '' : undefined}
          aria-hidden="true"
        />
      </ol>
      <span className="fl-story__all">
        <Link onPress={onAll}>Full history · {total}</Link>
      </span>
    </nav>
  );
}

function withCard(s: StoryStop, button: ReactElement) {
  return s.card ? (
    <HoverCard label={`${s.label}: ${s.says}`} fields={s.card}>
      {button}
    </HoverCard>
  ) : (
    button
  );
}
